import React, { FC, useState, useRef } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Modal, Button, Message } from 'semantic-ui-react'
import ExternalLink from '../common/ExternalLink'
import { useDatasetsCreator } from '../../contexts/DatasetsCreatorContext'
import './UploadCSVModal.scss'

const UploadCSVModal: FC<IProps> = ({ history }) => {
  const { currentStep, setCurrentStep, setOriginalCSV } = useDatasetsCreator()

  if (!setOriginalCSV || !setCurrentStep) {
    throw new Error('DatasetsCreator context was not initialized properly')
  }
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string>('')
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Check if modal should be open
  const isModalOpen = currentStep === 'upload'

  // File selection handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    validateAndSetFile(file)
  }

  // Validate and set file
  const validateAndSetFile = (file: File) => {
    // Only accept CSV files
    if (!file.name.endsWith('.csv')) {
      setFileError('Please upload a CSV file')
      setSelectedFile(null)
      return
    }

    setSelectedFile(file)
    setFileError('')
  }

  // Drag events
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) {
      setIsDragging(true)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0])
    }
  }

  // Click handler to trigger file input
  const handleAreaClick = () => {
    fileInputRef.current?.click()
  }

  // File upload handler
  const handleUpload = async () => {
    if (!selectedFile) {
      setFileError('Please select a file')
      return
    }

    try {
      // Read the file
      const content = await readFile(selectedFile)

      // Check for empty file
      if (!content.trim()) {
        setFileError('This file is empty.')
        return
      }

      // Parse CSV data
      const parsedData = parseCSV(content)

      // Save to context
      setOriginalCSV(parsedData)

      // Navigate to the next page
      setCurrentStep('')
      history.push('/create-dataset')
    } catch (error: any) {
      setFileError(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  }

  const readFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        resolve(e.target?.result as string)
      }
      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }
      reader.readAsText(file)
    })
  }

  const parseCSV = (content: string): string[][] => {
    const lines = content.split('\n')
    const result: string[][] = []
    let expectedLength: number | null = null

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue

      const elements = parseCSVLine(line)

      expectedLength = expectedLength === null ? elements.length : expectedLength

      if (elements.length !== expectedLength) {
        throw new Error(
          `This file contains invalid data - line ${i + 1} has ${
            elements.length
          } elements instead of the expected ${expectedLength}`
        )
      }

      result.push(elements)
    }

    return result
  }

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = []
    let currentElement = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]

      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        result.push(currentElement)
        currentElement = ''
      } else {
        currentElement += char
      }
    }

    result.push(currentElement)
    return result
  }

  const handleClose = () => {
    setCurrentStep('')
    setSelectedFile(null)
    setFileError('')
  }

  return (
    <Modal open={isModalOpen} onClose={handleClose} size="small">
      <Modal.Header>Upload CSV File</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <p>We recommend keeping your CSV to six columns or fewer, with one column acting as a unique identifier.</p>
          <p>
            Make sure you have the right to use this data, and that it has been collected in line with applicable privacy laws.
            All data is stored securely. Read <ExternalLink url="https://vloggi.com/privacy-policy">our data security policy</ExternalLink>.
          </p>
        </Modal.Description>
        <div
          className={`upload-area ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleAreaClick}
        >
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="file-input" />
          {selectedFile ? (
            <div>
              <p className="file-info">Selected file:</p>
              <p className="file-name">{selectedFile.name}</p>
            </div>
          ) : (
            <p className="prompt-text">Drag and drop or select from your computer</p>
          )}
        </div>
        {fileError && (
          <Message negative className="error-message">
            <Message.Header className="error-message">Error</Message.Header>
            <p>{fileError}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button primary onClick={handleUpload} disabled={!selectedFile}>
          Upload
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

interface IProps extends RouteComponentProps {}

export default withRouter(UploadCSVModal)
